/**
 * Import d'un export **Password Safe** XML (Password Safe 3.x de Bruce
 * Schneier, Windows / Linux, et ses clients pwSafe macOS / iOS : « Fichier >
 * Exporter vers > Format XML ». Ses utilisateurs doivent pouvoir partir avec
 * TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<passwordsafe delimiter="»">` porte des
 * `entry`, chacune ses `group` (niveaux separes par `.`, un point litteral
 * echappe en `\.`), `title`, `username`, `password`, `url`, `email`, `notes`
 * (retours a la ligne remplaces par le caractere `delimiter`), sa graine
 * `twofactorkey` et son historique `pwhistory`. Un alias (`[[groupe:titre:
 * identifiant]]`) ou un raccourci (`[~...~]`) reprend le mot de passe de sa
 * base. Tout champ non repris devient un champ personnalise, masque si son
 * libelle designe un secret.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';
import type { PasswordHistoryEntry } from './password-history.js';
import { serverHost } from './typed-fields.js';

/** Elements repris ailleurs, ou metadonnees sans valeur pour l'utilisateur (dates, politique, raccourcis clavier). */
const HANDLED_TAGS = new Set([
  'group',
  'title',
  'username',
  'password',
  'url',
  'email',
  'notes',
  'twofactorkey',
  'totpconfig',
  'totplength',
  'totptimestep',
  'totptimeinterval',
  'totpstarttime',
  'pwhistory',
  'uuid',
  'ctime',
  'ctimex',
  'atime',
  'atimex',
  'ltime',
  'ltimex',
  'xtime',
  'xtimex',
  'xtime_interval',
  'pmtime',
  'pmtimex',
  'rmtime',
  'rmtimex',
  'entrypolicy',
  'passwordpolicy',
  'passwordpolicyname',
  'symbols',
  'dca',
  'shiftdca',
  'kbshortcut',
  'protectedentry',
]);

/** Libelles lisibles des champs gardes en champ personnalise. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  autotype: 'Saisie automatique',
  runcommand: 'Commande',
  email: 'E-mail',
};

/** Alias `[[groupe:titre:identifiant]]` ou raccourci `[~groupe:titre:identifiant~]` vers une autre entree. */
const REFERENCE = /^(?:\[\[(.*)\]\]|\[~(.*)~\])$/su;

/** Le document Password Safe, ou `null` si le texte n'en est pas un. */
function passwordSafeRoot(text: string): XmlNode | null {
  if (!/^[\s﻿]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<passwordsafe[\s>]/u.test(text)) return null;
  let document: XmlNode;
  try {
    document = parseXml(text);
  } catch {
    return null;
  }
  const root = childNamed(document, 'passwordsafe');
  if (root === null) return null;
  return childNamed(root, 'entry') !== null || root.attributes['delimiter'] !== undefined ? root : null;
}

/** Texte brut d'un element enfant, jamais rogne (une espace peut faire partie d'un mot de passe). */
function rawOf(node: XmlNode, tag: string): string {
  return childNamed(node, tag)?.text ?? '';
}

/** Groupe `Banque.En\.ligne` -> `['Banque', 'En.ligne']` ; un point echappe reste dans le nom. */
function groupPath(raw: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < raw.length; i++) {
    const char = raw[i]!;
    if (char === '\\' && raw[i + 1] === '.') {
      current += '.';
      i++;
    } else if (char === '.') {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter((part) => part.length > 0);
}

/** Instant (ms) d'un horodatage : `<changedx>` ISO, ou `<changed><date/><time/></changed>` des anciens exports ; 0 si absent ou illisible. */
function timestampOf(node: XmlNode, tag: string): number {
  const iso = rawOf(node, `${tag}x`).trim();
  const changed = childNamed(node, tag);
  const legacy = changed === null ? '' : `${rawOf(changed, 'date').trim()}T${rawOf(changed, 'time').trim() || '00:00:00'}`;
  const parsed = Date.parse(iso || legacy);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Instant (ms) ou le mot de passe a ete pose : `pmtime` (modification du mot
 * de passe), a defaut `rmtime` (derniere modification de l'entree = borne
 * haute), jamais `ctime` (un mot de passe change depuis passerait pour
 * compromis). Aucune date sans mot de passe ; illisible : l'import vaut pose.
 */
function passwordChangedAtOf(entry: XmlNode, password: string): { passwordChangedAt?: number } {
  const passwordChangedAt = password.length > 0 ? timestampOf(entry, 'pmtime') || timestampOf(entry, 'rmtime') : 0;
  return passwordChangedAt > 0 ? { passwordChangedAt } : {};
}

/** Anciens mots de passe, plus recent d'abord, sans le mot de passe courant. */
function passwordHistory(entry: XmlNode, current: string): PasswordHistoryEntry[] {
  const history = childNamed(childNamed(entry, 'pwhistory'), 'history_entries');
  return childrenNamed(history, 'history_entry')
    .map((historyEntry) => ({ password: rawOf(historyEntry, 'oldpassword'), changedAt: timestampOf(historyEntry, 'changed') }))
    .filter((old) => old.password.length > 0 && old.password !== current)
    .sort((a, b) => b.changedAt - a.changedAt);
}

/**
 * Graine `twofactorkey` (base32) : chiffres ou periode non standard -> URI
 * `otpauth://` que `normalizeTotpSecret` garde entiere. Une graine qui n'est
 * pas du base32 reste un champ masque, jamais un TOTP faux.
 */
function totpOf(entry: XmlNode): { secret: string; extraNote: string; field: EntryCustomField | null } {
  const key = rawOf(entry, 'twofactorkey').trim();
  if (key.length === 0) return { secret: '', extraNote: '', field: null };
  const compact = key.replace(/[\s-]+/gu, '').toUpperCase();
  if (!/^otpauth:\/\//iu.test(key) && !/^[A-Z2-7]+=*$/u.test(compact)) {
    return { secret: '', extraNote: '', field: { name: 'Cle a deux facteurs', value: key, hidden: true } };
  }
  const digits = Number.parseInt(rawOf(entry, 'totplength').trim(), 10);
  const period = Number.parseInt((rawOf(entry, 'totptimestep') || rawOf(entry, 'totptimeinterval')).trim(), 10);
  const custom = (Number.isFinite(digits) && digits > 0 && digits !== 6) || (Number.isFinite(period) && period > 0 && period !== 30);
  if (!custom || /^otpauth:\/\//iu.test(key)) return { ...normalizeTotpSecret(key), field: null };
  const params = new URLSearchParams({ secret: compact });
  if (Number.isFinite(digits) && digits > 0) params.set('digits', String(digits));
  if (Number.isFinite(period) && period > 0) params.set('period', String(period));
  return { ...normalizeTotpSecret(`otpauth://totp/Password%20Safe?${params.toString()}`), field: null };
}

/** Cle `groupe:titre:identifiant` d'une entree, comme l'ecrit Password Safe dans un alias. */
function referenceKey(entry: XmlNode): string {
  return `${rawOf(entry, 'group').trim()}:${rawOf(entry, 'title').trim()}:${rawOf(entry, 'username').trim()}`;
}

/** Une entree Password Safe -> une entree (identifiant ou note). */
function passwordSafeEntry(entry: XmlNode, delimiter: string, bases: ReadonlyMap<string, XmlNode>): ImportedEntry {
  let password = rawOf(entry, 'password');
  const reference = REFERENCE.exec(password);
  // Un alias date son mot de passe comme sa base : c'est la base qui le pose.
  const base = reference === null ? undefined : bases.get((reference[1] ?? reference[2])!);
  if (base !== undefined) password = rawOf(base, 'password');
  const title = rawOf(entry, 'title').trim();
  const email = rawOf(entry, 'email').trim();
  let username = rawOf(entry, 'username').trim();
  const usernameIsEmail = username.length === 0 && email.length > 0;
  if (usernameIsEmail) username = email;
  const url = rawOf(entry, 'url').trim();
  const uris = url.length > 0 ? [url] : [];
  let notes = rawOf(entry, 'notes').replace(/\r\n?/gu, '\n');
  if (delimiter.length > 0) notes = notes.split(delimiter).join('\n');
  const totp = totpOf(entry);
  const customFields: EntryCustomField[] = [];
  if (email.length > 0 && !usernameIsEmail) customFields.push(csvColumnField(FIELD_LABELS['email']!, email));
  if (totp.field !== null) customFields.push(totp.field);
  for (const child of entry.children) {
    if (HANDLED_TAGS.has(child.tag) || child.text.trim().length === 0) continue;
    customFields.push(csvColumnField(FIELD_LABELS[child.tag] ?? child.tag, child.text.trim()));
  }
  const history = passwordHistory(entry, password);
  const folderPath = groupPath(rawOf(entry, 'group'));
  // Ni identifiant, ni mot de passe, ni adresse, ni TOTP : c'est une note securisee.
  const isNote = username.length === 0 && password.length === 0 && uris.length === 0 && totp.secret.length === 0;
  const host = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
  return {
    type: isNote ? 'note' : 'login',
    name: title || host || username || 'Sans nom',
    username,
    password,
    uris,
    notes: joinNotes([notes.trim(), totp.extraNote]),
    totpSecret: totp.secret,
    favorite: false,
    ...passwordChangedAtOf(base ?? entry, password),
    ...(history.length > 0 ? { passwordHistory: history } : {}),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(folderPath.length > 0 ? { folderPath } : {}),
  };
}

/** Un export Password Safe se reconnait a sa racine `<passwordsafe>` portant des `entry` ou son `delimiter`. */
export function isPasswordSafeXml(text: string): boolean {
  return passwordSafeRoot(text) !== null;
}

/** Parse un export Password Safe XML : chaque entree, dossiers, historique et TOTP compris. */
export function parsePasswordSafeXml(text: string): ImportedEntry[] {
  const root = passwordSafeRoot(text);
  if (root === null) throw new Error('export Password Safe : structure inattendue');
  const delimiter = root.attributes['delimiter'] ?? '';
  const entries = childrenNamed(root, 'entry');
  // Entrees de base, pour resoudre alias et raccourcis.
  const bases = new Map<string, XmlNode>();
  for (const entry of entries) {
    const password = rawOf(entry, 'password');
    if (!REFERENCE.test(password)) bases.set(referenceKey(entry), entry);
  }
  return entries.map((entry) => passwordSafeEntry(entry, delimiter, bases));
}
